import { useMemo, useCallback, useState, useEffect } from 'react';
import { getCaseStage } from 'services/case/caseServices';
import { totalCountEvent } from 'services/generalServices';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from 'modules/cases/config/queryClient';

export function useCaseStageList(
  excludeFromLoading = false,
  practice_area = '',
  options = {},
  loadFullList = true,
  limit = 10,
  isLandingPage = false
) {
  const { isCase = false, isLeads = false } = options;
  const queryClient = useQueryClient();
  const [currentPracticeArea, setCurrentPracticeArea] = useState(practice_area);
  const [caseStagePaginationToken, setCaseStagePaginationToken] = useState(null);

  // Update current practice area when prop changes
  useEffect(() => {
    setCurrentPracticeArea(practice_area);
  }, [practice_area]);

  // Build query key with all parameters for proper caching
  const queryKey = useMemo(() => {
    return queryKeys.caseStages.list(currentPracticeArea, isCase, isLeads, loadFullList);
  }, [currentPracticeArea, isCase, isLeads, loadFullList]);

  // React Query hook
  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      let pageLimit = limit;
      if (loadFullList) {
        const countResponse = await totalCountEvent('case-stage');
        pageLimit = countResponse?.data;
      }
      const response = await getCaseStage({
        practice_area: currentPracticeArea || false,
        pageLimit: pageLimit,
        excludeFromLoading,
      });

      setCaseStagePaginationToken(response?.data?.next_token || '');
      return response?.data?.case_stage || [];
    },
    staleTime: isCase || isLeads ? 10 * 60 * 1000 : 5 * 60 * 1000, // 10 minutes for cases/leads, 5 minutes otherwise
    gcTime: isCase || isLeads ? 30 * 60 * 1000 : 15 * 60 * 1000, // 30 minutes for cases/leads, 15 minutes otherwise
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: true,
  });

  const caseStageList = data || [];

  const caseStageOptions = useMemo(() => {
    if (caseStageList?.length) {
      let options = [];
      caseStageList.forEach((val) => {
        options.push({
          label: val.stage_name,
          value: val.stage_name,
        });
      });
      return options;
    } else {
      return [];
    }
  }, [caseStageList]);

  const loadStageList = useCallback(
    (newPracticeArea = false) => {
      if (newPracticeArea !== currentPracticeArea) {
        setCurrentPracticeArea(newPracticeArea);
        // Query will automatically refetch when currentPracticeArea changes
        // because it's part of the query key
      }
    },
    [currentPracticeArea]
  );

  useEffect(() => {
    refetch();
  }, [practice_area]);

  return { caseStageList, loadStageList, caseStageOptions, isStagesLoading: isLoading, error, caseStagePaginationToken };
}
